import { Box, CircularProgress, Stack, Typography } from '@mui/material';
import { ChevronDown, ChevronRight, File, Folder, FolderOpen } from 'lucide-react';
import React, { useState } from 'react';

const DEFAULT_FOLDERS = [
  { id: 'raw-data', name: 'Raw Data' },
  { id: 'analysis', name: 'Analysis' },
  { id: 'protocols', name: 'Protocols' },
  { id: 'reports', name: 'Reports' },
];

/**
 * FolderTree — collapsible folder structure with files.
 * Files are placed in "Raw Data" by default (all dld8/dld9 measurement files).
 * Other folders are empty placeholders for future organization.
 */
const FILE_STATUS_COLORS = {
  new: '#00CD93',
  modified: '#FFC107',
  deleted: '#EA4C7A',
};

const FolderTree = ({
  files = [],
  selectedFile,
  onSelectFile,
  onOpenFile,
  isLoading = false,
  fileStatuses = {},
}) => {
  const [expanded, setExpanded] = useState(['raw-data']);

  const toggleFolder = (id) => {
    setExpanded((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
  };

  // Place all measurement files in "Raw Data" folder,
  // including deleted files from fileStatuses that are no longer on disk
  const folderFiles = (folderId) => {
    if (folderId !== 'raw-data') return [];
    const existingNames = new Set(files.map((f) => f.filename));
    const deletedFiles = Object.entries(fileStatuses)
      .filter(([name, status]) => status === 'deleted' && !existingNames.has(name))
      .filter(([name]) => name.endsWith('.dld8') || name.endsWith('.dld9'))
      .map(([name]) => ({
        filename: name,
        display_name: name.endsWith('.dld9') ? name.replace('.dld9', '.dld8') : name,
        size_bytes: 0,
        modified_at: null,
        _deleted: true,
      }));
    return [...files, ...deletedFiles];
  };

  return (
    <Box sx={{ flex: 1, overflow: 'auto' }}>
      {DEFAULT_FOLDERS.map((folder) => {
        const isExpanded = expanded.includes(folder.id);
        const fFiles = folderFiles(folder.id);

        return (
          <Box key={folder.id}>
            {/* Folder header */}
            <Box
              onClick={() => toggleFolder(folder.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1.5,
                py: 0.6,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
              }}
            >
              {isExpanded ? (
                <ChevronDown size={14} strokeWidth={2} color="#7d8d97" />
              ) : (
                <ChevronRight size={14} strokeWidth={2} color="#7d8d97" />
              )}
              {isExpanded ? (
                <FolderOpen size={15} strokeWidth={1.5} color="#394b57" />
              ) : (
                <Folder size={15} strokeWidth={1.5} color="#394b57" />
              )}
              <Typography sx={{ fontSize: 13, fontWeight: 500, color: '#394b57', flex: 1 }}>
                {folder.name}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#7d8d97' }}>
                {fFiles.length > 0 ? fFiles.length : ''}
              </Typography>
            </Box>

            {/* Files */}
            {isExpanded && (
              <Box sx={{ pl: 2 }}>
                {fFiles.length === 0 && isLoading && folder.id === 'raw-data' && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 0.75 }}>
                    <CircularProgress size={12} sx={{ color: '#7d8d97' }} />
                    <Typography sx={{ fontSize: 11, color: '#7d8d97' }}>
                      Loading files...
                    </Typography>
                  </Box>
                )}
                {fFiles.length === 0 && !isLoading && (
                  <Typography
                    sx={{ px: 2, py: 0.5, fontSize: 11, color: '#A9B6BC', fontStyle: 'italic' }}
                  >
                    Empty
                  </Typography>
                )}
                {fFiles.map((f) => {
                  const status = fileStatuses[f.filename] || (f._deleted ? 'deleted' : null);
                  const statusColor = FILE_STATUS_COLORS[status];
                  const isDeleted = status === 'deleted';
                  return (
                    <Box
                      key={f.filename}
                      onClick={isDeleted ? undefined : () => onSelectFile(f)}
                      onDoubleClick={isDeleted ? undefined : () => onOpenFile?.(f)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.5,
                        py: 0.5,
                        cursor: isDeleted ? 'default' : 'pointer',
                        opacity: isDeleted ? 0.6 : 1,
                        borderRadius: 0.5,
                        mx: 0.5,
                        userSelect: 'none',
                        bgcolor:
                          !isDeleted && selectedFile?.filename === f.filename
                            ? 'rgba(57, 75, 87, 0.1)'
                            : 'transparent',
                        '&:hover': !isDeleted
                          ? {
                              bgcolor:
                                selectedFile?.filename === f.filename
                                  ? 'rgba(57, 75, 87, 0.1)'
                                  : 'rgba(0,0,0,0.04)',
                            }
                          : {},
                      }}
                    >
                      <File size={13} strokeWidth={1.5} color={statusColor || '#7d8d97'} />
                      <Typography
                        sx={{
                          fontSize: 12,
                          color: statusColor || '#394b57',
                          textDecoration: isDeleted ? 'line-through' : 'none',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={f.display_name || f.filename}
                      >
                        {f.display_name || f.filename}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default FolderTree;
